import { useTranslation } from 'react-i18next';
import { findLegacyOrderStage } from '../config/order-stages';

/**
 * The order-stage pill, reproducing the old panel's `StatusCell`:
 * 28px tall, 96px minimum width, fully rounded, text in the stage color over
 * a 10%-opacity wash of that same color.
 *
 * The wash is what makes this fiddly. The original layered an absolutely
 * positioned `::before` at `opacity: .1` behind the text, because MUI had only
 * the raw hex to work with. We get the same result in one element with
 * `color-mix(… 10%, transparent)`, which avoids the extra node and the
 * `z-index` juggling the original needed to keep its text above the wash.
 *
 * An unrecognized stage renders in neutral grey with the raw number, rather
 * than throwing or showing an empty pill — the old backend has been known to
 * introduce a stage before the frontend knows its name.
 */
export function LegacyStatusBadge({
  stage,
  solid = false,
  textColor,
  size = 'md',
  onClick,
}: {
  stage: number;
  /**
   * The old `StatusCell`'s `withoutOpacity`: a SOLID stage-color fill instead
   * of the 10% wash. The list cells and the drawer's desktop badge use it.
   */
  solid?: boolean;
  /** Text color override (the solid variants pass white, or black at stage 4). */
  textColor?: string;
  /** `sm` = the list's 10px/700 pill; `md` = the drawer's 16px pill. */
  size?: 'sm' | 'md';
  /** The old list badge was itself a click target opening the drawer. */
  onClick?: (() => void) | undefined;
}) {
  const { t } = useTranslation('legacy');
  const known = findLegacyOrderStage(stage);

  // Stage colors are per-stage CSS variables in the legacy token layer.
  const color = known ? `var(--lg-stage-${known.number})` : 'var(--lg-grey-500)';
  const label = known ? t(known.labelKey) : stage;

  const sizing =
    size === 'sm'
      ? 'text-[10px] leading-[11px] font-bold'
      : 'text-[16px] leading-[18.53px]';
  const style = solid
    ? { color: textColor ?? color, backgroundColor: color }
    : {
        color: textColor ?? color,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      };
  const base = `inline-flex h-7 min-w-24 items-center justify-center rounded-full px-3 text-center whitespace-nowrap ${sizing}`;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${base} cursor-pointer`}
        style={style}
      >
        {label}
      </button>
    );
  }

  return (
    <span className={base} style={style}>
      {label}
    </span>
  );
}
